import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import "./language-switcher.css";
import { isGermanPath, toGermanPath, toEnglishPath } from "../use-localized-path";

const languages = [
  { code: "en", key: "EN", name: "English", flag: "/assets/images/usa-flag.webp" },
  { code: "de", key: "DE", name: "Deutsch", flag: "/assets/images/germany-flag.webp" }
];

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const mouseInside = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Initialize only once
  useEffect(() => {
    const savedLang = localStorage.getItem("selectedLanguage");

    if (savedLang) {
      i18n.changeLanguage(savedLang).then(() => setLoading(false));
    } else {
      // ✅ First visit: use the browser's preferred language (de, de-DE, de-AT, de-CH…) — no external lookup
      const browserLang = (navigator.languages && navigator.languages[0]) || navigator.language || "";
      const lang = browserLang.toLowerCase().startsWith("de") ? "de" : "en";
      i18n.changeLanguage(lang);
      localStorage.setItem("selectedLanguage", lang);

      if (lang === "de" && !isGermanPath(location.pathname)) {
        navigate(toGermanPath(location.pathname), { replace: true });
      }
      setLoading(false);
    }
  }, [i18n, location.pathname, navigate]);

  // ✅ Language toggle logic with URL sync
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("selectedLanguage", lng);
    setOpen(false);

    const currentPath = location.pathname;

    if (lng === "de" && !isGermanPath(currentPath)) {
      // Add /de prefix
      navigate(toGermanPath(currentPath), { replace: true });
    } else if (lng === "en" && isGermanPath(currentPath)) {
      // Remove /de prefix
      navigate(toEnglishPath(currentPath), { replace: true });
    }
  };

  // ✅ Only show the other language in dropdown
  const currentLang =
    languages.find((lang) => lang.code === i18n.language) || languages[0];
  const otherLanguages = languages.filter(
    (lang) => lang.code !== currentLang.code
  );

  // ✅ Open on hover for mouse users; touch devices and keyboard still use click
  const handlePointerEnter = (e) => {
    if (e.pointerType !== "mouse") return;
    mouseInside.current = true;
    setOpen(true);
  };

  const handlePointerLeave = (e) => {
    if (e.pointerType !== "mouse") return;
    mouseInside.current = false;
    setOpen(false);
  };

  const handleButtonClick = () => {
    // Hover already opened it — don't let the click immediately close it again
    if (mouseInside.current) return;
    setOpen((prev) => !prev);
  };

  if (loading) return null;

  return (
    <div
      className="language-switcher"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <button className="lang-btn" type="button" onClick={handleButtonClick}>
        <img
          src={currentLang.flag}
          className="language-flag"
          alt={currentLang.code}
        />
        <span>{currentLang.key}</span>
        <i className="fa-solid fa-chevron-down"></i>
      </button>

      {open && (
        <ul className="language-dropdown">
          {otherLanguages.map((lang) => (
            <li key={lang.code} onClick={() => changeLanguage(lang.code)}>
              <img
                src={lang.flag}
                className="language-flag"
                alt={lang.code}
              />
              <span>{lang.key}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
